import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner, ListGroup, Badge, Card } from 'react-bootstrap';
import { getCustomersForDropdown } from '../../../../services/api/customerApi';

/**
 * RulesetConfigStep - Step 2: Select reference customer and copy rulesets.
 * For "existing customer" flow: selective copy with user-provided names.
 * For "new customer" flow: copy all rulesets from reference.
 */
const RulesetConfigStep = ({ wizard, api }) => {
  const [customers, setCustomers] = useState([]);
  const [selectedRef, setSelectedRef] = useState(wizard.referenceCustomerId || '');
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [refCheck, setRefCheck] = useState(null);
  const [copyResult, setCopyResult] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(true);

  // Selective copy state (for existing customer flow)
  const [refRulesets, setRefRulesets] = useState([]);
  const [selectedRulesetId, setSelectedRulesetId] = useState(null);
  const [newRulesetName, setNewRulesetName] = useState('');
  const [newShortCode, setNewShortCode] = useState('');

  const isExistingCustomerFlow = wizard.flowType === 'existing_customer_new_ruleset';

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await getCustomersForDropdown();
        setCustomers(response.data.items || []);
      } catch {
        // Handled gracefully
      } finally {
        setLoadingCustomers(false);
      }
    };
    fetchCustomers();
  }, []);

  const filteredCustomers = customers.filter(c =>
    (c.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
     c.customerCode.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSelectReference = async (customer) => {
    setSelectedRef(customer.id);
    setSelectedCustomerName(customer.customerName);
    setRefCheck(null);
    setRefRulesets([]);
    setSelectedRulesetId(null);
    try {
      const check = await api.checkReferenceCustomer(wizard.sessionId, customer.id);
      setRefCheck(check);
      // For existing customer flow, fetch the reference customer's rulesets
      if (isExistingCustomerFlow && check.rulesets) {
        setRefRulesets(check.rulesets);
      }
    } catch {
      // Error shown by hook
    }
  };

  const handleCopyRulesets = async () => {
    try {
      const requestData = isExistingCustomerFlow
        ? {
            referenceCustomerId: selectedRef,
            selectedRulesetIds: selectedRulesetId ? [selectedRulesetId] : [],
            newName: newRulesetName.trim(),
            newShortCode: newShortCode.trim()
          }
        : { referenceCustomerId: selectedRef };

      const result = await api.copyRulesets(wizard.sessionId, requestData);
      setCopyResult(result);
      wizard.setReferenceCustomerId(selectedRef);
      wizard.completeStep(2);
    } catch {
      // Error shown by hook
    }
  };

  if (copyResult) {
    return (
      <div>
        <Alert variant="success">
          <Alert.Heading>Rulesets Copied</Alert.Heading>
          <p>{copyResult.succeeded?.length || 0} ruleset(s) copied successfully.</p>
        </Alert>
        {copyResult.succeeded?.length > 0 && (
          <ListGroup className="mb-3">
            {copyResult.succeeded.map(r => (
              <ListGroup.Item key={r.id} className="d-flex justify-content-between">
                <span>{r.name} ({r.shortCode})</span>
                <Badge bg="secondary">{r.fieldCount} fields</Badge>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
        {copyResult.failed?.length > 0 && (
          <Alert variant="warning">
            <strong>{copyResult.failed.length} ruleset(s) failed:</strong>
            <ul className="mb-0 mt-1">
              {copyResult.failed.map((f, i) => <li key={i}>{f.name}: {f.error}</li>)}
            </ul>
          </Alert>
        )}
        <p className="text-muted">Click "Next" to continue.</p>
      </div>
    );
  }

  return (
    <div>
      <h5>Step 2: Ruleset Configuration</h5>
      <p className="text-muted">
        {isExistingCustomerFlow
          ? 'Select a reference customer, then choose which ruleset to copy and provide a name for the new ruleset.'
          : 'Select a reference customer to copy rulesets and extraction fields from.'}
      </p>

      {api.error && <Alert variant="danger" dismissible onClose={api.clearError}>{api.error}</Alert>}

      {/* Search and select reference customer */}
      <Form.Group className="mb-3">
        <Form.Label htmlFor="refCustomerSearch">Search Reference Customer</Form.Label>
        <Form.Control
          id="refCustomerSearch"
          type="text"
          placeholder="Type to search by name or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
        />
        {searchTerm.length > 0 && (
          <Form.Text className="text-muted">
            {filteredCustomers.length} customer(s) found
          </Form.Text>
        )}
      </Form.Group>

      {/* Results list */}
      {loadingCustomers ? (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" className="me-2" />
          <span className="text-muted">Loading customers...</span>
        </div>
      ) : searchTerm.length > 0 && filteredCustomers.length === 0 ? (
        <Alert variant="light" className="text-center text-muted border">
          No customers match &quot;{searchTerm}&quot;
        </Alert>
      ) : (
        <ListGroup className="mb-3" style={{ maxHeight: '200px', overflow: 'auto' }}>
          {(searchTerm.length > 0 ? filteredCustomers : filteredCustomers.slice(0, 10)).map(c => (
            <ListGroup.Item
              key={c.id}
              action
              active={selectedRef === c.id}
              onClick={() => handleSelectReference(c)}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{c.customerName}</strong>
                  <small className="text-muted ms-2">({c.customerCode})</small>
                </div>
                {selectedRef === c.id && <Badge bg="primary">Selected</Badge>}
              </div>
            </ListGroup.Item>
          ))}
          {searchTerm.length === 0 && filteredCustomers.length > 10 && (
            <ListGroup.Item disabled className="text-center text-muted">
              Type to search — showing first 10 of {filteredCustomers.length} customers
            </ListGroup.Item>
          )}
        </ListGroup>
      )}

      {/* Selected customer info */}
      {selectedRef && refCheck && (
        <Card className="mb-3">
          <Card.Body>
            <Card.Title className="h6">
              Selected: {selectedCustomerName}
            </Card.Title>
            <div className={refCheck.hasInvoiceDataCaptureRecords ? 'text-success' : 'text-warning'}>
              {refCheck.jobRecordCount} IDC job record(s)
              {refCheck.hasDataCaptureSchema && ' • Has data_capture_schema ✓'}
              {!refCheck.hasInvoiceDataCaptureRecords && (
                <div className="text-warning mt-1">
                  ⚠ No IDC records found — this customer cannot be used as reference for data_capture_schema
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* For existing customer flow: selective ruleset copy */}
      {isExistingCustomerFlow && selectedRef && refCheck && (
        <Card className="mb-3">
          <Card.Header><strong>Select Ruleset to Copy</strong></Card.Header>
          <Card.Body>
            {refRulesets.length > 0 ? (
              <ListGroup className="mb-3">
                {refRulesets.map(r => (
                  <ListGroup.Item
                    key={r.id}
                    action
                    active={selectedRulesetId === r.id}
                    onClick={() => {
                      setSelectedRulesetId(r.id);
                      if (!newRulesetName) setNewRulesetName(r.name);
                      if (!newShortCode) setNewShortCode(r.shortCode);
                    }}
                  >
                    <div className="d-flex justify-content-between">
                      <span>{r.name} ({r.shortCode})</span>
                      <Badge bg="secondary">{r.fieldCount} fields</Badge>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            ) : (
              <p className="text-muted">Loading rulesets from reference customer...</p>
            )}

            {selectedRulesetId && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="newRulesetName">New Ruleset Name *</Form.Label>
                  <Form.Control
                    id="newRulesetName"
                    type="text"
                    value={newRulesetName}
                    onChange={(e) => setNewRulesetName(e.target.value)}
                    placeholder="Enter name for the new ruleset"
                    maxLength={100}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="newShortCode">Short Code * (max 20 characters)</Form.Label>
                  <Form.Control
                    id="newShortCode"
                    type="text"
                    value={newShortCode}
                    onChange={(e) => setNewShortCode(e.target.value)}
                    placeholder="Enter short code"
                    maxLength={20}
                  />
                  <Form.Text className="text-muted">{newShortCode.length}/20 characters</Form.Text>
                </Form.Group>
              </>
            )}
          </Card.Body>
        </Card>
      )}

      <Button
        variant="primary"
        onClick={handleCopyRulesets}
        disabled={
          !selectedRef || api.loading ||
          (isExistingCustomerFlow && (!selectedRulesetId || !newRulesetName.trim() || !newShortCode.trim()))
        }
      >
        {api.loading ? (
          <><Spinner size="sm" className="me-2" />Copying...</>
        ) : isExistingCustomerFlow ? (
          'Copy Selected Ruleset'
        ) : (
          'Copy All Rulesets'
        )}
      </Button>
      {!selectedRef && (
        <Form.Text className="text-muted ms-2">Select a reference customer above to enable</Form.Text>
      )}
    </div>
  );
};

export default RulesetConfigStep;
